import {
  AddressType,
  Code,
  CodeStub,
  DecryptedAddress,
  DecryptedPatient,
  DecryptedPropertyStub,
  DecryptedTelecom,
  DecryptedTypedValue,
  Gender,
  PersonName,
  PersonNameUse,
  TelecomType,
  TypedValuesType,
} from '@icure/cardinal-sdk'
import { Button, DatePicker, Form, GetProps, Input, Popconfirm, Select } from 'antd'
import dayjs from 'dayjs'
import React, { ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useCreateOrUpdatePatientMutation } from '../../../../../core/api/patientApi'
import { getPatientDataFormated } from '../../../../../helpers/patientDataManipulations'
import { CustomModal } from '../../../../common/CustomModal'
import './index.less'
import { SpinLoader } from '../../../../common/SpinLoader'
import { allPatientsTagsEnum, PatientsTagsEnum, PatientsTagsKeyByValue } from '../../../../../helpers/types'
import { DATE_FORMAT_TO_DISPLAY, PATIENT_ID_CARD_IDENTIFIER } from '../../../../../constants'
import { useCreateOrUpdateCodesMutation, useGetCodesQuery } from '../../../../../core/api/codeApi'

type PatientForm = {
  firstName: string
  lastName: string
  emailAddress: string
  phoneNumber: string
  dateOfBirth?: number
  ssin: string
  idCardNumber?: string
  birthSex: Gender
  city: string
  street: string
  houseNumber: string
  postalCode: string
  tags: PatientsTagsEnum[]
}

interface ModalPatientFormProps {
  mode: 'edit' | 'create'
  isVisible: boolean
  onClose: () => void
  patientToEdit?: DecryptedPatient
}

type RangePickerProps = GetProps<typeof DatePicker.RangePicker>

export const ModalPatientForm = ({ mode, isVisible, onClose, patientToEdit }: ModalPatientFormProps): ReactElement => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    // Can not select days before today and today
    return current && current > dayjs().endOf('day')
  }
  const [form] = Form.useForm()

  const [
    createOrUpdatePatient,
    { error: createOrUpdatePatientError, isError: isCreateOrUpdatePatientError, isSuccess: isPatientCreatedOrUpdateSuccessfully, isLoading: isPatientCreatingOrUpdatingLoading },
  ] = useCreateOrUpdatePatientMutation()
  if (isCreateOrUpdatePatientError) {
    console.log(createOrUpdatePatientError)
  }
  const [createTags] = useCreateOrUpdateCodesMutation()
  const { data: customCodes } = useGetCodesQuery('RETINO_BRIDGE')
  const [newTagsToCreate, setNewTagsToCreate] = React.useState<string[]>([])

  const defaultTagsTranslated = allPatientsTagsEnum.map((tag) => {
    const key = PatientsTagsKeyByValue[tag as string]
    return key ? t(`patients.tags.${key}`) : tag
  })
  const defaultTagsOptions = defaultTagsTranslated.map((tag) => ({ label: tag, value: tag }))
  const customTagsOptions = (customCodes ?? []).map((tag) => ({ label: tag.code, value: tag.code }))

  const getPreparedUserData = (value: PatientForm) => {
    const { firstName, lastName, phoneNumber, emailAddress, dateOfBirth, birthSex, tags, city, street, houseNumber, postalCode, ssin, idCardNumber } = value
    const name = new PersonName({
      firstNames: [firstName],
      lastName: lastName,
      use: PersonNameUse.Official,
    })
    const dob = dateOfBirth ? dayjs(dateOfBirth) : undefined
    const fuzzyDateOfBirth = dob ? (dob.year() * 100 + dob.month() + 1) * 100 + dob.date() : undefined

    const address = new DecryptedAddress({
      addressType: AddressType.Home,
      telecoms: [
        ...(emailAddress
          ? [
              new DecryptedTelecom({
                telecomType: TelecomType.Email,
                telecomNumber: emailAddress,
              }),
            ]
          : []),
        ...(phoneNumber
          ? [
              new DecryptedTelecom({
                telecomType: TelecomType.Mobile,
                telecomNumber: phoneNumber,
              }),
            ]
          : []),
      ],
      city,
      street,
      houseNumber,
      postalCode,
    })
    const getTags = () => {
      return (tags ?? []).map((tag) => {
        const tagType = 'RETINO_BRIDGE'
        const tagVersion = '1'

        return new CodeStub({
          id: `${tagType}|${tag}|${tagVersion}`,
          type: tagType,
          code: tag,
          version: tagVersion,
        })
      })
    }

    const idCardProperty = new DecryptedPropertyStub({
      id: PATIENT_ID_CARD_IDENTIFIER,
      typedValue: new DecryptedTypedValue({
        type: TypedValuesType.String,
        stringValue: idCardNumber,
      }),
    })

    return {
      firstName,
      lastName,
      ssin,
      names: [name],
      dateOfBirth: fuzzyDateOfBirth,
      addresses: [address],
      tags: getTags(),
      birthSex,
      properties: [idCardProperty],
    }
  }
  const handleSubmit = (value: PatientForm) => {
    if (mode === 'create') createOrUpdatePatient(new DecryptedPatient(getPreparedUserData(value)))
    if (mode === 'edit') createOrUpdatePatient(new DecryptedPatient({ ...patientToEdit, ...getPreparedUserData(value) }))

    form.resetFields()
  }
  const getPatientToEdit = (patient: DecryptedPatient) => {
    const { firstName, lastName, dateOfBirth, phoneNumber, ssin, idCardNumber, emailAddress, tags, birthSex, city, street, houseNumber, postalCode } = getPatientDataFormated(
      patient,
      t,
      currentLanguage,
    )
    return {
      firstName,
      lastName,
      dateOfBirth: dateOfBirth ? dayjs(dateOfBirth.toString().replace(/(....)(..)(..)/, '$1-$2-$3')) : undefined,
      phoneNumber,
      emailAddress,
      ssin,
      idCardNumber,
      birthSex,
      city,
      street,
      houseNumber,
      postalCode,
      tags: tags?.map((tag) => tag.code) ?? [],
    }
  }

  useEffect(() => {
    if (isPatientCreatedOrUpdateSuccessfully) {
      onClose()
    }
  }, [isPatientCreatedOrUpdateSuccessfully])

  const handleOnClose = () => {
    form.resetFields()
    onClose()
  }

  const { Option } = Select

  const getCustomFooter = () => {
    const getPopconfirmDescription = (codes: string[]) => {
      const isPlural = codes.length > 1
      const joinedCodes = codes.join(', ')
      const keyStart = isPlural ? 'modalPatientForm.popconfirm.saveLabels.start.other' : 'modalPatientForm.popconfirm.saveLabels.start.one'
      const keyEnd = isPlural ? 'modalPatientForm.popconfirm.saveLabels.end.other' : 'modalPatientForm.popconfirm.saveLabels.end.one'

      return (
        <p className="popconfirmDescription">
          {t(keyStart)} <span className="labelList">{joinedCodes}</span> {t(keyEnd)}
        </p>
      )
    }

    return [
      <Button key="back" onClick={handleOnClose}>
        {t('modalPatientForm.labels.cancel')}
      </Button>,

      newTagsToCreate.length !== 0 ? (
        <Popconfirm
          key="confirmClose"
          title={t('modalPatientForm.popconfirm.title')}
          description={getPopconfirmDescription(newTagsToCreate)}
          onConfirm={() => {
            createTags(
              newTagsToCreate.map(
                (tag) =>
                  new Code({
                    id: `RETINO_BRIDGE|${tag}|1`,
                    code: tag,
                    type: 'RETINO_BRIDGE',
                    version: '1',
                  }),
              ),
            )
            setNewTagsToCreate([])
            form.submit()
          }}
          onCancel={() => {
            form.submit()
            setNewTagsToCreate([])
          }}
          okText={t('common.yes')}
          cancelText={t('common.no')}
          placement="topRight"
        >
          <Button key="submit" type="primary">
            {t('modalPatientForm.labels.save')}
          </Button>
        </Popconfirm>
      ) : (
        <Button key="submit" type="primary" onClick={() => form.submit()}>
          {t('modalPatientForm.labels.save')}
        </Button>
      ),
    ]
  }

  return (
    <>
      <CustomModal
        isVisible={isVisible}
        handleClose={() => handleOnClose()}
        customFooter={getCustomFooter()}
        title={mode === 'edit' ? t('modalPatientForm.modalEditTitle') : t('modalPatientForm.modalCreateTitle')}
      >
        <div className="modalPatientForm">
          {isPatientCreatingOrUpdatingLoading && <SpinLoader />}
          <Form
            className="modalPatientForm__form"
            layout="vertical"
            colon={false}
            form={form}
            onFinish={(values) => handleSubmit(values)}
            initialValues={mode === 'edit' && patientToEdit ? getPatientToEdit(patientToEdit) : {}}
          >
            <div className="modalPatientForm__form__infoBlock">
              <h3 className="modalPatientForm__form__infoBlock__title">{t('modalPatientForm.labels.personalTitle')}</h3>
              <div className="modalPatientForm__form__infoBlock__inputs">
                <Form.Item name="firstName" label={t('modalPatientForm.labels.firstName')} rules={[{ required: true, message: t('modalPatientForm.labels.required.firstName') }]}>
                  <Input placeholder={t('modalPatientForm.labels.firstName')} size="large" />
                </Form.Item>
                <Form.Item name="lastName" label={t('modalPatientForm.labels.lastName')} rules={[{ required: true, message: t('modalPatientForm.labels.required.lastName') }]}>
                  <Input placeholder={t('modalPatientForm.labels.lastName')} size="large" />
                </Form.Item>
                <Form.Item name="dateOfBirth" label={t('modalPatientForm.labels.dateOfBirth')}>
                  <DatePicker
                    format={DATE_FORMAT_TO_DISPLAY}
                    placeholder={t('modalPatientForm.labels.dateOfBirth')}
                    size="large"
                    style={{ width: '100%' }}
                    disabledDate={disabledDate}
                  />
                </Form.Item>
                <Form.Item name="birthSex" label={t('modalPatientForm.labels.birthSex')}>
                  <Select placeholder={t('modalPatientForm.labels.birthSex')} size="large">
                    <Option value={Gender.Male}>{Gender.Male}</Option>
                    <Option value={Gender.Female}>{Gender.Female}</Option>
                  </Select>
                </Form.Item>
              </div>
            </div>

            <div className="modalPatientForm__form__infoBlock">
              <h3 className="modalPatientForm__form__infoBlock__title">{t('modalPatientForm.labels.adminTitle')}</h3>
              <div className="modalPatientForm__form__infoBlock__inputs">
                <Form.Item name="ssin" label={t('modalPatientForm.labels.ssin')} rules={[{ required: true, message: t('modalPatientForm.labels.required.ssin') }]}>
                  <Input placeholder={t('modalPatientForm.labels.ssin')} size="large" />
                </Form.Item>
                <Form.Item name="idCardNumber" label={t('modalPatientForm.labels.idCardNumber')}>
                  <Input placeholder={t('modalPatientForm.labels.idCardNumber')} size="large" />
                </Form.Item>
              </div>
            </div>

            <div className="modalPatientForm__form__infoBlock">
              <h3 className="modalPatientForm__form__infoBlock__title">{t('modalPatientForm.labels.contactTitle')}</h3>
              <div className="modalPatientForm__form__infoBlock__inputs">
                <Form.Item name="phoneNumber" label={t('modalPatientForm.labels.phoneNumber')}>
                  <Input placeholder={t('modalPatientForm.labels.phoneNumber')} size="large" />
                </Form.Item>
                <Form.Item name="emailAddress" label={t('modalPatientForm.labels.emailAddress')}>
                  <Input placeholder={t('modalPatientForm.labels.emailAddress')} size="large" />
                </Form.Item>
              </div>
            </div>

            <div className="modalPatientForm__form__infoBlock">
              <h3 className="modalPatientForm__form__infoBlock__title">{t('modalPatientForm.labels.domicileTitle')}</h3>
              <div className="modalPatientForm__form__infoBlock__inputs">
                <Form.Item name="city" label={t('modalPatientForm.labels.city')}>
                  <Input placeholder={t('modalPatientForm.labels.city')} size="large" />
                </Form.Item>
                <Form.Item name="street" label={t('modalPatientForm.labels.street')}>
                  <Input placeholder={t('modalPatientForm.labels.street')} size="large" />
                </Form.Item>
                <Form.Item name="houseNumber" label={t('modalPatientForm.labels.houseNumber')}>
                  <Input placeholder={t('modalPatientForm.labels.houseNumber')} size="large" />
                </Form.Item>
                <Form.Item name="postalCode" label={t('modalPatientForm.labels.postalCode')}>
                  <Input placeholder={t('modalPatientForm.labels.postalCode')} size="large" />
                </Form.Item>
              </div>
            </div>

            <div className="modalPatientForm__form__infoBlock">
              <h3 className="modalPatientForm__form__infoBlock__title">{t('modalPatientForm.labels.tagsTitle')}</h3>
              <div className="modalPatientForm__form__infoBlock__inputs">
                <Form.Item name="tags" label={t('modalPatientForm.labels.tags')}>
                  <Select
                    placeholder={t('modalPatientForm.labels.tags')}
                    size="large"
                    mode="tags"
                    onChange={(values) => {
                      const defaultTagsValues = defaultTagsOptions?.map((item) => item.value as string)
                      const customTagsValues = customTagsOptions?.map((item) => item.value)
                      const unknownTagsValues = values.filter((item: string) => !customTagsValues.includes(item)).filter((item: string) => !defaultTagsValues.includes(item))
                      setNewTagsToCreate(unknownTagsValues)
                    }}
                    options={[
                      {
                        label: t('patientInfo.tags.standard'),
                        options: defaultTagsOptions,
                      },
                      {
                        label: t('patientInfo.tags.custom'),
                        options: customTagsOptions,
                      },
                    ]}
                  />
                </Form.Item>
              </div>
            </div>
          </Form>
        </div>
      </CustomModal>
    </>
  )
}
